import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier.tsx';
import { TextInput, Select } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import {
	useTranslationCommonForm,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	UPDATE_PROGRAMME_FORM_FIELDS,
	UpdateProgrammeSettingsInputsValues,
	UpdateProgrammeShape,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeGeneralSettings.types';
import { ProgrammeBankAccountSection } from '@dealstack/frontend/app/pages/Project/MIP/components/ProgrammeBankAccount/ProgrammeBankAccountSection';
import { DataBankAccountTct, SummaryMipTct } from '@dealstack/shared/lib/generated';
import { CURRENCY_OPTIONS } from '@dealstack/shared/lib/format/currency/currency.ts';
import { Stack, Group, Flex, Button, Divider } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';

interface Props {
	programme: SummaryMipTct;
	bankAccountDetails?: DataBankAccountTct;
	isLoading: boolean;
	showEntityField: boolean;
	handleUpdateProgramme: (
		formData: UpdateProgrammeSettingsInputsValues,
		bankAccountId?: string
	) => void;
}

export const ProgrammeForm = ({
	bankAccountDetails,
	programme,
	isLoading,
	showEntityField,
	handleUpdateProgramme,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonForm = useTranslationCommonForm();

	const programmeSettingsForm = useForm<UpdateProgrammeSettingsInputsValues>({
		mode: 'onChange',
		defaultValues: {
			[UPDATE_PROGRAMME_FORM_FIELDS.ENTITY_NAME]: programme?.entity?.displayName,
			[UPDATE_PROGRAMME_FORM_FIELDS.PROGRAMME_NAME]: programme?.programmeName,
			[UPDATE_PROGRAMME_FORM_FIELDS.CURRENCY]: programme?.currency,
			[UPDATE_PROGRAMME_FORM_FIELDS.INVESTOR_INSTRUCTIONS]:
				bankAccountDetails?.investorInstructions ?? '',
		},
		resolver: zodResolver(UpdateProgrammeShape(tCommonForm)),
	});

	return (
		<Flex direction="column" justify="space-between" h="100%" style={{ flex: 1 }}>
			<ContainerWithHeader
				title={tMipManage('settings_modal.about_label')}
				headerActions={
					<Button
						type="submit"
						data-testid={TEST_ELEMENT_IDS.PROGRAMME_UPDATE_SETTINGS_BUTTON}
						disabled={!programmeSettingsForm.formState.isDirty || isLoading}
						onClick={programmeSettingsForm.handleSubmit((formData) =>
							handleUpdateProgramme(formData, bankAccountDetails?.id)
						)}
					>
						{tMipManage('settings_modal.update_programme')}
					</Button>
				}
			>
				<FormProvider {...programmeSettingsForm}>
					<form>
						<Stack gap="md">
							<Group align="flex-start" grow>
								<Stack gap="md" style={{ flex: 1 }}>
									{showEntityField && (
										<TextInput
											label={tMipManage('settings_modal.entity_name')}
											name={UPDATE_PROGRAMME_FORM_FIELDS.ENTITY_NAME}
											size="md"
											disabled
											data-testid={
												TEST_ELEMENT_IDS.MIP_MANAGE_PROJECT_SETTINGS_ENTITY_NAME_INPUT
											}
										/>
									)}
									<Select
										label={tMipManage('settings_modal.currency')}
										name={UPDATE_PROGRAMME_FORM_FIELDS.CURRENCY}
										size="md"
										searchable
										data={CURRENCY_OPTIONS}
									/>
								</Stack>
								<Stack gap="md" style={{ flex: 1 }}>
									<TextInput
										label={tMipManage('settings_modal.programme_name')}
										name={UPDATE_PROGRAMME_FORM_FIELDS.PROGRAMME_NAME}
										size="md"
										data-testid={
											TEST_ELEMENT_IDS.MIP_MANAGE_PROJECT_SETTINGS_PROGRAMME_NAME_INPUT
										}
									/>
								</Stack>
							</Group>

							<Divider />

							<ProgrammeBankAccountSection
								bankAccount={bankAccountDetails}
								instructionsFieldName={
									UPDATE_PROGRAMME_FORM_FIELDS.INVESTOR_INSTRUCTIONS
								}
							/>
						</Stack>
					</form>
				</FormProvider>
			</ContainerWithHeader>
		</Flex>
	);
};
